import { useState } from 'react';
import { clsx } from 'clsx';
import {
  DOCUMENT_TYPES,
  FIELDS_BY_DOC_TYPE,
  DocumentType,
} from '@/types/onboarding';
import { InfoTooltip } from '@components/common/InfoTooltip';

interface ExtractionFieldsStepProps {
  extractionFields: Record<string, string[]>;
  onChange: (fields: Record<string, string[]>) => void;
}

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  purchase_order: 'Purchase Orders',
  receipt: 'Receipts',
  invoice: 'Invoices',
};

export const ExtractionFieldsStep = ({
  extractionFields,
  onChange,
}: ExtractionFieldsStepProps) => {
  const [activeTab, setActiveTab] = useState<DocumentType>(DOCUMENT_TYPES[0]);

  const fields = FIELDS_BY_DOC_TYPE[activeTab];
  const selectedKeys = extractionFields[activeTab] ?? [];

  const isSelected = (key: string) => selectedKeys.includes(key);

  const toggleField = (key: string) => {
    const updated = isSelected(key)
      ? selectedKeys.filter((k) => k !== key)
      : [...selectedKeys, key];

    onChange({ ...extractionFields, [activeTab]: updated });
  };

  const selectAll = () => {
    onChange({
      ...extractionFields,
      [activeTab]: fields.map((f) => f.key),
    });
  };

  const deselectAll = () => {
    onChange({ ...extractionFields, [activeTab]: [] });
  };

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Document Extraction Fields</h2>
          <InfoTooltip>Choose the data extracted for each document type.</InfoTooltip>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8" aria-label="Document type tabs">
          {DOCUMENT_TYPES.map((docType) => {
            const total = FIELDS_BY_DOC_TYPE[docType].length;
            const selected = (extractionFields[docType] ?? []).length;
            const isActive = docType === activeTab;

            return (
              <button
                key={docType}
                type="button"
                onClick={() => setActiveTab(docType)}
                className={clsx(
                  'whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:text-gray-300'
                )}
              >
                {DOC_TYPE_LABELS[docType]}
                <span
                  className={clsx(
                    'ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    isActive
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  )}
                >
                  {selected}/{total}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Select all / Deselect all */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={selectAll}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={deselectAll}
          className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          Deselect all
        </button>
      </div>

      {/* Field cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((field) => {
          const checked = isSelected(field.key);

          return (
            <label
              key={field.key}
              className={clsx(
                'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                checked
                  ? 'border-primary-600 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                  : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleField(field.key)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-primary-400"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {field.label}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};
